import { Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SmoothLink as Link } from "@/components/navigation";

const pricingData = [
  {
    treatment: "Single Dental Implant",
    india: "$500 - $800",
    usa: "$3,000 - $5,000",
    uk: "$2,500 - $4,000",
    savings: "Up to 85%",
  },
  {
    treatment: "All-on-4 Implants",
    india: "$5,000 - $8,000",
    usa: "$25,000 - $35,000",
    uk: "$20,000 - $28,000",
    savings: "Up to 80%",
  },
  {
    treatment: "Full Mouth Rehabilitation",
    india: "$4,000 - $10,000",
    usa: "$30,000 - $50,000",
    uk: "$25,000 - $40,000",
    savings: "Up to 87%",
  },
  {
    treatment: "Heart Bypass Surgery",
    india: "$5,000 - $8,000",
    usa: "$70,000 - $150,000",
    uk: "$30,000 - $50,000",
    savings: "Up to 95%",
  },
  {
    treatment: "Hip Replacement",
    india: "$6,000 - $9,000",
    usa: "$40,000 - $70,000",
    uk: "$15,000 - $25,000",
    savings: "Up to 87%",
  },
];

const inclusions = [
  "All medical procedures",
  "Hospital stay",
  "Doctor consultations",
  "Airport pickup & drop",
  "Dedicated care coordinator",
  "Post-treatment follow-up",
];

export function PricingComparisonSection() {
  return (
    <section id="pricing" className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-12 lg:mb-16">
          <span className="inline-block text-primary font-medium text-sm uppercase tracking-wider mb-4">
            Transparent Pricing
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-secondary mb-4">
            Save Up to 90% on Healthcare
          </h2>
          <p className="text-foreground/70 text-lg">
            Compare treatment costs in India vs. USA & UK. All prices include hospital stay and concierge support.
          </p>
        </div>

        {/* Pricing Table */}
        <div className="bg-card rounded-lg shadow-soft overflow-hidden mb-12">
          {/* Table Header */}
          <div className="grid grid-cols-5 bg-secondary text-secondary-foreground font-medium text-sm">
            <div className="p-4 lg:p-6 col-span-2">Treatment</div>
            <div className="p-4 lg:p-6 text-center bg-primary">
              <div className="font-display font-semibold">India</div>
              <div className="text-xs text-primary-foreground/80">TrustedHeal Price</div>
            </div>
            <div className="p-4 lg:p-6 text-center">USA</div>
            <div className="p-4 lg:p-6 text-center">UK</div>
          </div>

          {/* Table Body */}
          {pricingData.map((row, index) => (
            <div
              key={row.treatment}
              className={`grid grid-cols-5 items-center text-sm ${
                index % 2 === 0 ? "bg-background" : "bg-muted/50"
              }`}
            >
              <div className="p-4 lg:p-6 col-span-2">
                <div className="font-medium text-secondary">{row.treatment}</div>
                <div className="text-xs text-accent font-semibold mt-1">{row.savings}</div>
              </div>
              <div className="p-4 lg:p-6 text-center bg-primary/5">
                <span className="font-semibold text-primary">{row.india}</span>
              </div>
              <div className="p-4 lg:p-6 text-center text-muted-foreground line-through">
                {row.usa}
              </div>
              <div className="p-4 lg:p-6 text-center text-muted-foreground line-through">
                {row.uk}
              </div>
            </div>
          ))}
        </div>

        {/* What's Included */}
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div>
            <h3 className="font-display text-2xl font-bold text-secondary mb-6">
              What's Included in Your Package
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {inclusions.map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-accent" />
                  </div>
                  <span className="text-foreground">{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-primary to-accent rounded-lg p-8 text-center">
            <h3 className="font-display text-2xl font-bold text-primary-foreground mb-4">
              Get Your Personalized Quote
            </h3>
            <p className="text-primary-foreground/90 mb-6">
              Our AI assistant will analyze your needs and provide a detailed cost estimate within minutes.
            </p>
            <Link to="/contact">
              <Button 
                variant="secondary" 
                className="rounded-full px-8 font-medium bg-card text-primary hover:bg-card/90 gap-2"
              >
                Calculate Your Savings
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
